import type { Database } from '../types/supabase';
import { directSupabase as supabase } from './supabaseClient';

type EmailTemplateRow = Database['public']['Tables']['email_templates']['Row'];
type EmailTemplateInsert = Database['public']['Tables']['email_templates']['Insert'];

export type EmailTemplateType =
  | 'glassOrder'
  | 'windowOrder'
  | 'enduranceDoors'
  | 'roofReplacement'
  | 'keystoneIntroduction'
  | 'firstSurveyAppointmentRequest'
  | 'firstSurveyAppointmentConfirmation'
  | 'finalSurveyAppointmentConfirmation'
  | 'sendCustomerEstimate'
  | 'sendEstimateQuote'
  | 'sendCustomerInvoice'
  | 'finalSurveyAppointmentRequest'
  | 'orderGoods'
  | 'installationDateConfirmation'
  | 'deliveryDateConfirmation'
  | 'otherKeystoneServices'
  | 'completionEmail'
  | 'requestReview'
  | 'outstandingPayment'
  | 'depositRequest'
  | 'notificationEmail'
  | 'newsletter';

export interface CalendarEventAttendee {
  emailAddress: {
    address: string;
    name: string;
  };
  type: string;
}

export type EmailTemplate = EmailTemplateRow & {
  // Remove old calendar event columns since we're using calendar_event_settings JSONB column
};

export interface CalendarEventSettings {
  subject: string | null;
  body: string | null;
  duration_minutes: number | null;
  location: string | null;
  attendees: Array<{
    emailAddress: {
      address: string;
      name: string;
    };
    type: string;
  }> | null;
}

export interface SavedNewsletterTemplate {
  id: string;
  template_type: 'newsletter';
  subject: string;
  template: string;
  is_saved_template: true;
  saved_template_name: string;
  template_category: 'user';
  version: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  display_name: string;
}

export const emailTemplates = {
  /**
   * Get the latest version of all templates
   * If organizationName is provided, returns ONLY templates for that organization (no defaults)
   * If no organizationName is provided, returns only default templates (organization_name IS NULL)
   */
  getAll: async (organizationName?: string | null) => {
    try {
      let query = supabase
        .from('email_templates')
        .select('*')
        .eq('is_active', true);

      if (organizationName) {
        // Get ONLY organization-specific templates (no defaults)
        query = query.eq('organization_name', organizationName);
      } else {
        // Get only default templates if no organization specified
        query = query.is('organization_name', null);
      }

      const { data, error } = await query.order('template_type', { ascending: true });

      return { data, error };
    } catch (error: any) {
      console.error('getAll error:', error);
      return { data: null, error: error.message || 'Failed to load templates' };
    }
  },

  /**
   * Get all templates for a specific organization (including default fallbacks)
   */
  getByOrganization: async (organizationName: string) => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('is_active', true)
        .or(`organization_name.eq.${organizationName},organization_name.is.null`)
        .order('template_type', { ascending: true })
        .order('organization_name', { ascending: false, nullsFirst: false }); // Org-specific first

      return { data, error };
    } catch (error: any) {
      console.error('getByOrganization error:', error);
      return { data: null, error: error.message || 'Failed to load templates' };
    }
  },

  /**
   * Get the latest version of a specific template type
   * If organizationName is provided, returns ONLY organization-specific template (no fallback to defaults)
   * If no organizationName is provided, returns default template (organization_name IS NULL)
   */
  getByType: async (
    templateType: EmailTemplate['template_type'],
    organizationName?: string | null
  ) => {
    try {
      if (organizationName) {
        // Get ONLY organization-specific template (no fallback to defaults)
        const { data, error } = await supabase
          .from('email_templates')
          .select('*')
          .eq('template_type', templateType)
          .eq('organization_name', organizationName)
          .eq('is_active', true)
          .order('version', { ascending: false })
          .limit(1)
          .maybeSingle();

        return { data, error };
      }

      // Get default template (organization_name IS NULL) only if no organization specified
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('template_type', templateType)
        .is('organization_name', null)
        .eq('is_active', true)
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle();

      return { data, error };
    } catch (error: any) {
      console.error('getByType error:', error);
      return { data: null, error: error.message || 'Failed to load template' };
    }
  },

  /**
   * Get all versions of a template type
   */
  getVersions: async (templateType: EmailTemplate['template_type']) => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('template_type', templateType)
        .order('created_at', { ascending: false });

      return { data, error };
    } catch (error: any) {
      console.error('getVersions error:', error);
      return { data: null, error: error.message || 'Failed to load template versions' };
    }
  },

  /**
   * Get all newsletter templates (including saved templates)
   */
  getNewsletterTemplates: async () => {
    try {
      const { data, error } = await supabase
        .from('newsletter_templates')
        .select('*')
        .order('display_name', { ascending: true });

      return { data, error };
    } catch (error: any) {
      console.error('getNewsletterTemplates error:', error);
      return { data: null, error: error.message || 'Failed to load newsletter templates' };
    }
  },

  /**
   * Get saved newsletter templates for the current user
   */
  getSavedNewsletterTemplates: async () => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('template_type', 'newsletter')
        .eq('is_saved_template', true)
        .eq('created_by', (await supabase.auth.getUser()).data.user?.id)
        .order('saved_template_name', { ascending: true });

      return { data, error };
    } catch (error: any) {
      console.error('getSavedNewsletterTemplates error:', error);
      return { data: null, error: error.message || 'Failed to load saved newsletter templates' };
    }
  },

  /**
   * Save a newsletter template
   */
  saveNewsletterTemplate: async (templateData: {
    subject: string;
    template: string;
    saved_template_name: string;
  }) => {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Determine next available version for 'newsletter' templates to avoid unique constraint conflicts
      const { data: latest } = await supabase
        .from('email_templates')
        .select('version')
        .eq('template_type', 'newsletter')
        .order('version', { ascending: false })
        .limit(1)
        .single();

      const nextVersion = ((latest as any)?.version || 0) + 1;

      const { data, error } = await supabase
        .from('email_templates')
        .insert({
          template_type: 'newsletter',
          subject: templateData.subject,
          template: templateData.template,
          is_saved_template: true,
          saved_template_name: templateData.saved_template_name,
          template_category: 'user',
          is_default: false,
          is_active: false,
          version: nextVersion,
          created_by: user.id
        })
        .select()
        .single();

      return { data, error };
    } catch (error: any) {
      console.error('saveNewsletterTemplate error:', error);
      return { data: null, error: error.message || 'Failed to save newsletter template' };
    }
  },

  /**
   * Update a saved newsletter template
   */
  updateSavedNewsletterTemplate: async (templateId: string, templateData: {
    subject?: string;
    template?: string;
    saved_template_name?: string;
  }) => {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('email_templates')
        .update({
          ...templateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', templateId)
        .eq('created_by', user.id)
        .eq('is_saved_template', true)
        .select()
        .single();

      return { data, error };
    } catch (error: any) {
      console.error('updateSavedNewsletterTemplate error:', error);
      return { data: null, error: error.message || 'Failed to update newsletter template' };
    }
  },

  /**
   * Delete a saved newsletter template
   */
  deleteSavedNewsletterTemplate: async (templateId: string) => {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', templateId)
        .eq('created_by', user.id)
        .eq('is_saved_template', true)
        .select()
        .single();

      return { data, error };
    } catch (error: any) {
      console.error('deleteSavedNewsletterTemplate error:', error);
      return { data: null, error: error.message || 'Failed to delete newsletter template' };
    }
  },

  /**
   * Create a new version of a template
   */
  create: async (template: EmailTemplateInsert) => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .insert(template)
        .select()
        .single();

      return { data, error };
    } catch (error: any) {
      console.error('create error:', error);
      return { data: null, error: error.message || 'Failed to create template' };
    }
  },

  /**
   * Restore a template to its default version
   */
  restoreDefault: async (templateType: EmailTemplate['template_type']) => {
    try {
      // Find the default template
      const { data: defaultTemplate, error: findError } = await supabase
        .from('email_templates')
        .select('*')
        .eq('template_type', templateType)
        .eq('is_default', true)
        .single()

      if (findError || !defaultTemplate) {
        return { data: null, error: 'Default template not found' };
      }

      // Unset current active template
      await supabase
        .from('email_templates')
        .update({ is_active: false })
        .eq('template_type', templateType)
        .eq('is_active', true)

      // Set default template as active
      const { data, error } = await supabase
        .from('email_templates')
        .update({ is_active: true })
        .eq('id', defaultTemplate.id)
        .select()
        .single()

      if (error) {
        return { data: null, error: error.message };
      }

      // Cleanup: delete all templates for this type where both is_default = false and is_active = false
      await supabase
        .from('email_templates')
        .delete()
        .eq('template_type', templateType)
        .eq('is_default', false)
        .eq('is_active', false)

      return { data, error: null };
    } catch (error: any) {
      console.error('restoreDefault error:', error);
      return { data: null, error: error.message || 'Failed to restore default template' };
    }
  },

  /**
   * Get the default template for a type
   */
  getDefault: async (templateType: EmailTemplate['template_type']) => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('template_type', templateType)
        .eq('is_default', true)
        .single();
      return { data, error };
    } catch (error: any) {
      console.error('getDefault error:', error);
      return { data: null, error: error.message || 'Failed to get default template' };
    }
  },

  /**
   * Set the current template as default for its type
   */
  setDefault: async ({
    template_type,
    subject,
    template,
    calendar_event_settings
  }: {
    template_type: EmailTemplate['template_type'];
    subject: string;
    template: string;
    calendar_event_settings: CalendarEventSettings | null;
  }) => {
    try {
      // Unset previous default
      await supabase
        .from('email_templates')
        .update({ is_default: false })
        .eq('template_type', template_type)
        .eq('is_default', true);

      // Unset previous active
      await supabase
        .from('email_templates')
        .update({ is_active: false })
        .eq('template_type', template_type)
        .eq('is_active', true);

      // Set the new default
      const { error } = await supabase
        .from('email_templates')
        .update({ 
          is_default: true, 
          is_active: true,
          subject,
          template,
          calendar_event_settings
        })
        .eq('template_type', template_type)
        .eq('subject', subject)
        .eq('template', template);

      return { error };
    } catch (error: any) {
      console.error('setDefault error:', error);
      return { error: error.message || 'Failed to set default template' };
    }
  },

  updateDefaultTemplate: async (id: string, updates: { subject: string, template: string }) => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .update({ ...updates, is_active: true }) // ensure it's also active
        .eq('id', id)
        .select()
        .single();
      return { data, error };
    } catch (error: any) {
      console.error('updateDefaultTemplate error:', error);
      return { data: null, error: error.message || 'Failed to update default template' };
    }
  },

  async getCalendarEventDetails(templateType: EmailTemplateType, variables: Record<string, string>): Promise<{
    subject: string;
    body: string;
    durationMinutes: number;
    location: string;
    attendees: CalendarEventAttendee[];
  }> {
    try {
      const { data: template, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('template_type', templateType)
        .eq('is_active', true)
        .single();

      if (error || !template) {
        throw new Error(`No template found for type: ${templateType}`);
      }

      if (!template.calendar_event_settings || typeof template.calendar_event_settings !== 'object') {
        throw new Error(`No calendar event template found for type: ${templateType}`);
      }

      const settings = template.calendar_event_settings as unknown as CalendarEventSettings;
      if (!settings.subject || !settings.body) {
        throw new Error(`No calendar event template found for type: ${templateType}`);
      }

      // Helper function for case-insensitive variable replacement
      const processTemplateString = (text: string, variables: Record<string, string>): string => {
        const lowercasedVariables: Record<string, string> = {};
        for (const key in variables) {
          lowercasedVariables[key.toLowerCase()] = variables[key];
        }
        return text.replace(/\{\{(\w+)\}\}/gi, (match, key) => {
          const lowerKey = key.toLowerCase();
          if (Object.prototype.hasOwnProperty.call(lowercasedVariables, lowerKey)) {
            return lowercasedVariables[lowerKey];
          }
          return match;
        });
      };

      // Replace variables in calendar event subject and body
      const subject = processTemplateString(settings.subject, variables);
      const body = processTemplateString(settings.body, variables);
      const location = processTemplateString(settings.location || '', variables);

      // Process attendees
      const attendees = settings.attendees?.map((attendee: CalendarEventAttendee) => {
        const processedAttendee = { ...attendee };
        processedAttendee.emailAddress.address = processTemplateString(attendee.emailAddress.address, variables);
        processedAttendee.emailAddress.name = processTemplateString(attendee.emailAddress.name, variables);
        return processedAttendee;
      }) || [];

      return {
        subject,
        body,
        durationMinutes: settings.duration_minutes || 60,
        location,
        attendees
      };
    } catch (error: any) {
      console.error('getCalendarEventDetails error:', error);
      throw error;
    }
  },

  save: async (template: EmailTemplateInsert, asDefault: boolean = false) => {
    try {
      const organizationName = (template as any).organization_name || null;
      
      // Determine next version number for this template type and organization
      let versionQuery = supabase
        .from('email_templates')
        .select('version')
        .eq('template_type', template.template_type);
      
      if (organizationName) {
        versionQuery = versionQuery.eq('organization_name', organizationName);
      } else {
        versionQuery = versionQuery.is('organization_name', null);
      }
      
      const { data: latest } = await versionQuery
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle();

      const nextVersion = (latest?.version || 0) + 1;

      // Clear flags on existing rows for this organization
      if (asDefault) {
        // Unset previous default and active for this organization
        let defaultQuery = supabase
          .from('email_templates')
          .update({ is_default: false })
          .eq('template_type', template.template_type)
          .eq('is_default', true);
        
        if (organizationName) {
          defaultQuery = defaultQuery.eq('organization_name', organizationName);
        } else {
          defaultQuery = defaultQuery.is('organization_name', null);
        }
        await defaultQuery;
        
        let activeQuery = supabase
          .from('email_templates')
          .update({ is_active: false })
          .eq('template_type', template.template_type)
          .eq('is_active', true);
        
        if (organizationName) {
          activeQuery = activeQuery.eq('organization_name', organizationName);
        } else {
          activeQuery = activeQuery.is('organization_name', null);
        }
        await activeQuery;
      } else {
        // Only unset previous active for this organization
        let activeQuery = supabase
          .from('email_templates')
          .update({ is_active: false })
          .eq('template_type', template.template_type)
          .eq('is_active', true);
        
        if (organizationName) {
          activeQuery = activeQuery.eq('organization_name', organizationName);
        } else {
          activeQuery = activeQuery.is('organization_name', null);
        }
        await activeQuery;
      }

      // Insert a new row as the latest version
      const { data, error } = await supabase
        .from('email_templates')
        .insert({
          ...template,
          version: nextVersion,
          is_active: true,
          is_default: asDefault,
          organization_name: organizationName
        })
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      // Optional cleanup of stale rows (non-default and inactive) for this organization
      let cleanupQuery = supabase
        .from('email_templates')
        .delete()
        .eq('template_type', template.template_type)
        .eq('is_default', false)
        .eq('is_active', false);
      
      if (organizationName) {
        cleanupQuery = cleanupQuery.eq('organization_name', organizationName);
      } else {
        cleanupQuery = cleanupQuery.is('organization_name', null);
      }
      await cleanupQuery;

      return { data, error: null };
    } catch (error: any) {
      console.error('save error:', error);
      return { data: null, error: error.message || 'Failed to save template' };
    }
  }
}; 